/**
 * Main survey component
 *
 * This will validate and show the proper survey component based on
 * different conditions.
 */
import { memo, useMemo } from 'react';

import NPSComponent from '~/components/survey/NPSComponent';
import RadioList from '~/components/survey/RadioList';
import useSurvey from '~/hooks/survey/useSurvey';
import useUser from '~/lib/user';
import {
  NPS_SURVEY_EXPERIMENT_NAME,
  RADIO_LIST_SURVEY_EXPERIMENT_NAME,
  showNPSSurvey,
  showRadioListSurvey,
} from '~/lib/util/constants';
import { useRouter } from 'next/router';
import { SurveyContext } from '~/contexts/survey';
import { SurveyContextProps } from '~/types/survey/types';
import { useExperiment } from '~/lib/experiment';

export default memo(function Survey() {
  const { asPath } = useRouter();
  const { user } = useUser();
  const {
    NPSSurvey,
    radioListSurvey,
    surveys,
    hasNPS,
    isProcessing,
    dismiss,
    submit,
    getAll,
  } = useSurvey();

  // Enroll only if there are surveys
  const { variant: NPSVariant } = useExperiment(
    NPS_SURVEY_EXPERIMENT_NAME,
    !surveys.length,
  );
  const { variant: radioListVariant } = useExperiment(
    RADIO_LIST_SURVEY_EXPERIMENT_NAME,
    !surveys.length,
  );

  const context: SurveyContextProps = useMemo(
    () => ({
      surveys,
      NPSSurvey,
      hasNPS,
      isProcessing,
      getAll,
      dismiss,
      submit,
    }),
    [surveys, NPSSurvey, hasNPS, isProcessing, getAll, dismiss, submit],
  );

  const getSurveyComponent = () => {
    if (!user?.id || !surveys.length) {
      return null;
    }

    if (NPSVariant === 'enabled' && showNPSSurvey(asPath) && NPSSurvey) {
      return (
        <SurveyContext.Provider value={context}>
          <NPSComponent survey={NPSSurvey} />
        </SurveyContext.Provider>
      );
    }

    if (
      radioListVariant === 'enabled' &&
      showRadioListSurvey(asPath) &&
      radioListSurvey
    ) {
      return (
        <SurveyContext.Provider value={context}>
          <RadioList survey={radioListSurvey} />
        </SurveyContext.Provider>
      );
    }

    return null;
  };

  return getSurveyComponent();
});
